/**
 * Inbox list header chrome: title icon, sort/filter actions, status segments.
 */
import {
  AlertTriangle,
  ArrowUpDown,
  ChevronDown,
  Gauge,
  Inbox,
  KeyRound,
  ListFilter,
  Receipt,
  User,
  UserPlus,
  UserX,
  Users,
  Wrench,
} from "lucide-react";
import { useTranslations } from "next-intl";
import type { ReactNode } from "react";

import type { InboxSidebarSelection } from "./module-inbox-data";

function HeaderAction(props: {
  readonly icon: ReactNode;
  readonly label: string;
}): ReactNode {
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 6,
        padding: "6px 11px",
        borderRadius: 8,
        cursor: "default",
      }}
    >
      {props.icon}
      {props.label}
    </span>
  );
}

export function StatusSegment(props: {
  readonly icon: ReactNode;
  readonly label: string;
  readonly active?: boolean;
}): ReactNode {
  const active = props.active === true;
  return (
    <span
      style={{
        display: "inline-flex",
        alignItems: "center",
        gap: 7,
        padding: "6px 13px",
        borderRadius: 8,
        fontSize: 13,
        fontWeight: active ? 600 : 400,
        color: active ? "#0B0F19" : "rgba(11,15,25,0.55)",
        background: active ? "#fff" : "transparent",
        boxShadow: active ? "0 1px 2px rgba(11,15,25,0.06)" : "none",
      }}
    >
      {props.icon}
      {props.label}
    </span>
  );
}

export function InboxListHeaderIcon(props: {
  readonly selection: InboxSidebarSelection;
}): ReactNode {
  const iconProps = { width: 18, height: 18, strokeWidth: 1.9 };
  const wrap = (icon: ReactNode): ReactNode => (
    <span style={{ color: "rgba(11,15,25,0.55)", display: "inline-flex" }}>
      {icon}
    </span>
  );

  if (props.selection.kind === "mine") return wrap(<User {...iconProps} />);
  if (props.selection.kind === "all") return wrap(<Users {...iconProps} />);
  if (props.selection.kind === "unassigned") {
    return wrap(<UserX {...iconProps} />);
  }

  const boardIconById: Record<typeof props.selection.boardId, ReactNode> = {
    allgemein: <Inbox {...iconProps} />,
    schaeden: <AlertTriangle {...iconProps} />,
    schluesselbestellungen: <KeyRound {...iconProps} />,
    mietinteressenten: <UserPlus {...iconProps} />,
    rechnungen: <Receipt {...iconProps} />,
    wartungen: <Wrench {...iconProps} />,
    zaehlerstaende: <Gauge {...iconProps} />,
  };

  return wrap(boardIconById[props.selection.boardId]);
}

export function InboxListFilterActions(): ReactNode {
  const t = useTranslations("marketing.moduleInboxListChrome");
  return (
    <div
      style={{
        display: "flex",
        alignItems: "center",
        gap: 6,
        fontSize: 13,
        color: "rgba(11,15,25,0.6)",
      }}
    >
      <HeaderAction
        icon={<ArrowUpDown width={14} height={14} strokeWidth={1.8} />}
        label={t("sortierung")}
      />
      <HeaderAction
        icon={<ListFilter width={14} height={14} strokeWidth={1.8} />}
        label={t("filter")}
      />
      <span
        style={{
          display: "inline-flex",
          alignItems: "center",
          gap: 6,
          border: "1px solid #E7E9ED",
          borderRadius: 8,
          padding: "6px 11px",
          color: "#0B0F19",
          fontWeight: 500,
          cursor: "default",
        }}
      >
        {t("alle")} <ChevronDown width={14} height={14} strokeWidth={1.8} />
      </span>
    </div>
  );
}
